import { useEffect, useRef } from "react";

interface HeroSectionProps {
  onGetQuote: () => void;
}

export default function HeroSection({ onGetQuote }: HeroSectionProps) {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const trustRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [headlineRef.current, subRef.current, ctaRef.current, trustRef.current];
    els.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      setTimeout(() => {
        if (!el) return;
        el.style.transition = "opacity 0.9s ease, transform 0.9s ease";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, 200 + i * 180);
    });
  }, []);

  return (
    <section
      className="relative w-full flex items-center justify-center overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/bb8281d58c1bbea667ac7b60a8c988fd.jpg')`,
        }}
      />

      {/* Layered Overlay */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(8,8,8,0.85) 0%, rgba(8,8,8,0.60) 50%, rgba(8,8,8,0.80) 100%)" }} />

      {/* Subtle texture line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 lg:px-12 flex flex-col items-center text-center pt-24">

        {/* Trust micro-line */}
        <div ref={trustRef} className="mb-8 flex items-center gap-3">
          <div className="w-12 h-px bg-stone-400/60" />
          <span
            className="text-stone-300/90 text-xs uppercase tracking-[0.28em] font-medium"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Commercial &amp; Institutional Projects Across Ontario
          </span>
          <div className="w-12 h-px bg-stone-400/60" />
        </div>

        {/* H1 */}
        <h1
          ref={headlineRef}
          className="text-white font-semibold leading-[1.05] mb-7"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(2.8rem, 6.5vw, 5.8rem)",
            textShadow: "0 2px 40px rgba(0,0,0,0.3)",
            letterSpacing: "-0.01em",
          }}
        >
          Commercial Construction
          <br />
          <em className="font-light not-italic" style={{ color: "rgba(255,255,255,0.88)" }}>Precision-Built Across Ontario</em>
        </h1>

        {/* Subheadline */}
        <p
          ref={subRef}
          className="text-stone-200/85 mb-10 max-w-2xl leading-relaxed"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "clamp(1rem, 1.6vw, 1.15rem)",
            fontWeight: 300,
            letterSpacing: "0.01em",
          }}
        >
          General contracting, design/build, leasehold improvements, and project management
          delivered with structural integrity and clean execution.
        </p>

        {/* CTAs */}
        <div ref={ctaRef} className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={onGetQuote}
            className="px-10 py-4 bg-white text-stone-900 font-semibold text-sm uppercase tracking-[0.12em] rounded-sm hover:bg-stone-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Get a Free Quote
          </button>
          <a
            href="tel:4167241172"
            className="flex items-center gap-2.5 px-10 py-4 border border-white/50 text-white font-medium text-sm uppercase tracking-[0.12em] rounded-sm hover:bg-white/10 hover:border-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <i className="ri-phone-line text-sm"></i>
            Call Now
          </a>
        </div>

        {/* Scroll cue */}
        <div className="mt-20 flex flex-col items-center gap-2">
          <div
            className="w-px h-12 bg-gradient-to-b from-transparent to-white/40"
            style={{ animation: "pulse 2s ease-in-out infinite" }}
          />
          <i className="ri-arrow-down-s-line text-white/40 text-lg"></i>
        </div>
      </div>
    </section>
  );
}
